(function () {
  const controllers = new Map();

  class EditorialWaitlistController {
    constructor(root) {
      this.root = root;
      this.openingTl = null;
      this.context = null;
      this.mediaQuery = null;
      this.observer = null;
      this.refreshFrame = null;
      this.scrollHintHandler = null;
      this.triggers = [];
    }

    mount() {
      this.focusSuccess();
      this.root.classList.add('is-enhanced');
      this.setupScrollHintDismissal();

      if (this.root.dataset.animationsEnabled !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return;

      if (!EditorialWaitlistController._registered) {
        window.gsap.registerPlugin(window.ScrollTrigger);
        EditorialWaitlistController._registered = true;
      }

      this.root.classList.add('is-motion-ready');

      this.setupOpeningReveal();
      this.setupDesktopMotion();
      this.setupPanelStatus();
      this.setupMobileReveals();
      this.scheduleRefresh();
    }

    setupOpeningReveal() {
      const introReveals = this.root.querySelectorAll(
        '.editorial-waitlist__panel--intro [data-waitlist-reveal]',
      );
      const revealOffset = window.matchMedia('(max-width: 989px)').matches ? 24 : 48;

      this.openingTl = window.gsap.timeline();
      if (introReveals.length) {
        this.openingTl.fromTo(
          introReveals,
          { opacity: 0, y: revealOffset },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            stagger: 0.1,
            ease: 'power2.out',
          },
          0.3,
        );
      }
    }

    setupScrollHintDismissal() {
      const scrollHint = this.root.querySelector('.editorial-waitlist__scroll-hint');
      if (!scrollHint) return;

      const dismiss = () => {
        scrollHint.classList.add('is-dismissed');
        scrollHint.setAttribute('aria-hidden', 'true');
        this.scrollHintHandler = null;
      };

      this.scrollHintHandler = dismiss;
      if (window.scrollY > 0) {
        dismiss();
        return;
      }

      window.addEventListener('scroll', dismiss, { once: true, passive: true });
    }

    setupDesktopMotion() {
      this.mediaQuery = window.gsap.matchMedia();

      this.mediaQuery.add('(min-width: 990px) and (prefers-reduced-motion: no-preference)', () => {
        const root = this.root;
        this.setupIntroExit();

        const panels = [
          root.querySelector('.editorial-waitlist__panel--statement'),
          root.querySelector('.editorial-waitlist__panel--production'),
          root.querySelector('.editorial-waitlist__panel--signup'),
        ];
        panels.forEach((panel, index) => {
          this.setupPanelReveal(panel);
          if (index < panels.length - 1) this.setupPanelExit(panel);
        });

        return () => {};
      });
    }

    setupIntroExit() {
      const intro = this.root.querySelector('.editorial-waitlist__panel--intro');
      if (!intro) return;

      const exitTargets = intro.querySelectorAll('.editorial-waitlist__panel-inner');
      if (!exitTargets.length) return;

      window.gsap.to(exitTargets, {
        opacity: 0,
        y: -24,
        duration: 1,
        stagger: 0.06,
        ease: 'none',
        scrollTrigger: {
          trigger: intro,
          start: 'top top',
          end: '+=42%',
          scrub: 0.35,
        },
      });
    }

    setupPanelReveal(panel) {
      if (!panel) return;
      const reveals = panel.querySelectorAll('[data-waitlist-reveal]');
      if (!reveals.length) return;

      const revealTimeline = window.gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: panel,
          start: 'top 88%',
          end: 'top 48%',
          scrub: 0.35,
        },
      });

      revealTimeline.fromTo(
        reveals,
        { opacity: 0, y: 48 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.08,
        },
      );
    }

    setupPanelExit(panel) {
      if (!panel) return;
      const panelInner = panel.querySelector('.editorial-waitlist__panel-inner');
      if (!panelInner) return;

      window.gsap.to(panelInner, {
        opacity: 0,
        y: -32,
        duration: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: panel,
          start: 'bottom 62%',
          end: 'bottom 35%',
          scrub: 0.35,
        },
      });
    }

    scheduleRefresh() {
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = null;
        if (controllers.get(this.root) === this) {
          window.ScrollTrigger.refresh();
        }
      });
    }

    setupMobileReveals() {
      const mobileQuery = window.matchMedia('(max-width: 989px)');

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!mobileQuery.matches) return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const element = entry.target;
            this.observer.unobserve(element);
            window.gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 0.7,
              ease: 'power2.out',
            });
          });
        },
        { threshold: 0.15 },
      );

      const reveals = this.root.querySelectorAll(
        '.editorial-waitlist__panel--statement [data-waitlist-reveal],' +
          '.editorial-waitlist__panel--production [data-waitlist-reveal],' +
          '.editorial-waitlist__panel--signup [data-waitlist-reveal]',
      );
      reveals.forEach((element) => this.observer.observe(element));
    }

    setupPanelStatus() {
      const statusElement = this.root.querySelector('[data-waitlist-status]');
      if (!statusElement) return;

      this.root.querySelectorAll('[data-waitlist-panel]').forEach((panel) => {
        const trigger = window.ScrollTrigger.create({
          trigger: panel,
          start: 'top center',
          end: 'bottom center',
          onToggle: (self) => {
            if (!self.isActive) return;
            const status = panel.dataset.panelStatus;
            if (status != null) {
              statusElement.textContent = status;
            }
          },
        });
        this.triggers.push(trigger);
      });
    }

    focusSuccess() {
      const success = this.root.querySelector('[data-waitlist-success]');
      if (success) {
        success.focus({ preventScroll: true });
      }
    }

    destroy() {
      if (this.scrollHintHandler) {
        window.removeEventListener('scroll', this.scrollHintHandler);
        this.scrollHintHandler = null;
      }
      if (this.refreshFrame != null) {
        window.cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.mediaQuery) {
        this.mediaQuery.revert();
        this.mediaQuery = null;
      }
      if (this.openingTl) {
        this.openingTl.revert();
        this.openingTl = null;
      }
      this.triggers.forEach((trigger) => trigger.kill());
      this.triggers = [];
      this.root.classList.remove('is-motion-ready', 'is-enhanced');
    }
  }

  function mountWithin(container) {
    const scope = container || document;
    const roots = scope.querySelectorAll ? scope.querySelectorAll('[data-hoezo-waitlist]') : [];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      const controller = new EditorialWaitlistController(root);
      controllers.set(root, controller);
      controller.mount();
    });
  }

  function destroyWithin(container) {
    if (!container || !container.querySelectorAll) return;
    container.querySelectorAll('[data-hoezo-waitlist]').forEach((root) => {
      const controller = controllers.get(root);
      if (controller) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => mountWithin(), { once: true });
  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
